/**
 * Chat Input Hook
 *
 * Main hook for managing chat input form state and submission logic.
 * Composes smaller hooks for specific functionality.
 *
 * @module features/chat/hooks/useChatInput
 */

import {
  useState,
  useEffect,
  useRef,
  FormEvent,
  useCallback,
  useMemo,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Message } from "../types/sdk";
import { useFileUpload } from "@/hooks/use-file-upload";
import { ensureToolCallsHaveResponses } from "../lib/ensure-tool-responses";
import { deleteAudioFromStorage } from "../services/audio-upload-service";
import { deleteImageFromStorage } from "../services/image-upload-service";
import { deletePdfFromStorage } from "../services/pdf-upload-service";
import {
  deleteCaptureMessage,
  appendCaptureContent,
  type AppendContentBlock,
} from "../services/capture-service";
import { FEATURES } from "@/config";
import type {
  StorageAudioBlock,
  StorageImageBlock,
  StoragePdfBlock,
  ContentBlock,
} from "../types/messages";
import { getImageFileName } from "../types/messages";

// Modular hooks
import {
  useInputPersistence,
  useAutoUpload,
  useAudioUpload,
  useAudioFileUpload,
  getInputStorageKey,
  getBlocksStorageKey,
} from "./chat-input";

// =============================================================================
// TYPES
// =============================================================================

export interface UseInputFormOptions {
  messages: Message[];
  isLoading: boolean;
  artifactContext: any;
  onSubmit: (payload: any, options: any) => void;
  onResetFirstToken: () => void;
  userId?: string;
  conversationId?: string;
  onBeforeSubmit?: () => Promise<void>;
  captureMode?: boolean;
  initialContentBlocks?: ContentBlock[];
}

export interface UseInputFormReturn {
  // Input state
  input: string;
  setInput: (value: string) => void;

  // File upload
  contentBlocks: ContentBlock[];
  setContentBlocks: (blocks: ContentBlock[]) => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dropRef: React.RefObject<HTMLDivElement | null>;
  removeBlock: (index: number) => void;
  resetBlocks: () => void;
  dragOver: boolean;
  handlePaste: (
    e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  updateBlockDescription: (
    index: number,
    description: string,
    descriptionAudioUrls?: string[],
  ) => void;

  // Audio recording
  isRecording: boolean;
  recordingDuration: number;
  analyserNode: AnalyserNode | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  isUploadingAudio: boolean;

  // Form submission
  handleSubmit: (e: FormEvent) => void;
  canSubmit: boolean;

  // Capture mode validation helpers
  hasAudioRecordings: boolean;
  hasImages: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for managing chat input form state and submission logic.
 *
 * Features:
 * - Manages text input state
 * - Integrates file upload via useFileUpload hook
 * - Handles form submission with multimodal content
 * - Ensures tool calls have responses before submission
 * - Supports optimistic UI updates
 * - Capture mode for auto-saving content to Firestore
 */
export function useChatInput({
  messages,
  isLoading,
  artifactContext,
  onSubmit,
  onResetFirstToken,
  userId,
  conversationId,
  onBeforeSubmit,
  captureMode = false,
  initialContentBlocks = [],
}: UseInputFormOptions): UseInputFormReturn {
  const [input, setInput] = useState("");

  // Ref for audio file upload function (allows passing to useFileUpload before hook is called)
  const uploadAudioFileRef = useRef<((file: File) => Promise<void>) | null>(
    null,
  );
  const handleAudioFile = useCallback(async (file: File) => {
    console.log(
      "📤 [useChatInput] handleAudioFile called for:",
      file.name,
      "type:",
      file.type,
    );
    if (uploadAudioFileRef.current) {
      await uploadAudioFileRef.current(file);
    } else {
      console.error(
        "❌ [useChatInput] uploadAudioFileRef.current is null - audio file upload not ready",
      );
    }
  }, []);

  // File upload integration
  const {
    contentBlocks,
    setContentBlocks,
    handleFileUpload,
    dropRef,
    removeBlock: removeBlockFromHook,
    resetBlocks: resetBlocksFromHook,
    dragOver,
    handlePaste,
    updateBlockDescription: updateBlockDescriptionFromHook,
  } = useFileUpload({
    enabled: FEATURES.fileUploadEnabled,
    initialBlocks: initialContentBlocks,
    onAudioFile: handleAudioFile,
  });

  // Sync initialContentBlocks when they're loaded asynchronously (e.g., from Firestore)
  useEffect(() => {
    if (initialContentBlocks.length > 0 && contentBlocks.length === 0) {
      console.log(
        "[useChatInput] Setting initial content blocks:",
        initialContentBlocks.length,
      );
      setContentBlocks(initialContentBlocks);
    }
  }, [initialContentBlocks, contentBlocks.length, setContentBlocks]);

  // Auto-upload for images and PDFs
  const { processedBlocksRef, savedBlockIdsRef, hasPendingUploads } =
    useAutoUpload({
      contentBlocks,
      setContentBlocks,
      userId,
      conversationId,
      captureMode,
    });

  // Audio FILE upload (for uploaded audio files, not recordings)
  const { uploadAudioFile } = useAudioFileUpload({
    userId,
    conversationId,
    setContentBlocks,
    captureMode,
    savedBlockIdsRef,
  });

  // Update ref when uploadAudioFile changes
  useEffect(() => {
    uploadAudioFileRef.current = uploadAudioFile;
  }, [uploadAudioFile]);

  // Audio recording and upload
  const {
    isRecording,
    recordingDuration,
    analyserNode,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    isUploadingAudio,
  } = useAudioUpload({
    userId,
    conversationId,
    contentBlocks,
    setContentBlocks,
    captureMode,
    savedBlockIdsRef,
  });

  // Input persistence across conversations
  useInputPersistence({
    conversationId,
    captureMode,
    input,
    contentBlocks,
    setInput,
    setContentBlocks,
    onConversationChange: () => {
      processedBlocksRef.current.clear();
      savedBlockIdsRef.current.clear();
    },
  });

  // Enhanced updateBlockDescription with Firestore persistence in capture mode
  const updateBlockDescription = useCallback(
    async (
      idx: number,
      description: string,
      descriptionAudioUrls?: string[],
    ) => {
      updateBlockDescriptionFromHook(idx, description, descriptionAudioUrls);

      if (captureMode && userId && conversationId) {
        const block = contentBlocks[idx];

        if (
          block &&
          "source_type" in block &&
          block.source_type === "storage" &&
          "messageId" in block &&
          block.messageId
        ) {
          try {
            const blocksToAppend: AppendContentBlock[] = [];

            if (description && description.trim()) {
              blocksToAppend.push({ type: "text", text: description });
            }

            if (descriptionAudioUrls && descriptionAudioUrls.length > 0) {
              // Get image reference for audio descriptions
              const imageBlock = block as StorageImageBlock;
              const imagePath = imageBlock.relative_gcs_path;
              const imageRef = imagePath ? `![img](${imagePath})` : undefined;

              for (const audioUrl of descriptionAudioUrls) {
                blocksToAppend.push({
                  type: "document",
                  source: {
                    type: "text",
                    media_type: "text/plain",
                    data: "", // Empty - transcript added later by backend
                  },
                  title: imageRef
                    ? `Audio transcript voor image_ref: ${imageRef}`
                    : "Audio transcript",
                  citations: { enabled: true },
                  metadata: {
                    audio: {
                      gcs_path: audioUrl,
                      mime_type: "audio/webm",
                    },
                    ...(imageRef && { image_ref: imageRef }),
                  },
                });
              }
            }

            if (blocksToAppend.length > 0) {
              await appendCaptureContent(
                userId,
                conversationId,
                block.messageId,
                blocksToAppend,
              );
              console.log(`[CaptureMode] ✅ Description saved to Firestore`);
            }
          } catch (error) {
            console.error(
              "[CaptureMode] Failed to save description to Firestore:",
              error,
            );
          }
        }
      }
    },
    [
      updateBlockDescriptionFromHook,
      captureMode,
      userId,
      conversationId,
      contentBlocks,
    ],
  );

  // Enhanced removeBlock with Firebase Storage deletion
  const removeBlock = useCallback(
    async (index: number) => {
      const block = contentBlocks[index];
      if (!block) {
        removeBlockFromHook(index);
        return;
      }

      if (userId && conversationId) {
        try {
          if (block.type === "audio" && block.source_type === "storage") {
            const audioBlock = block as StorageAudioBlock;
            const fileName =
              audioBlock.relative_gcs_path.split("/").pop() || "";
            await deleteAudioFromStorage(userId, conversationId, fileName);
            console.log("✅ Audio deleted from storage:", fileName);
          } else if (
            block.type === "image" &&
            block.source_type === "storage"
          ) {
            const imageBlock = block as StorageImageBlock;
            const fileName = getImageFileName(imageBlock);
            if (fileName) {
              await deleteImageFromStorage(userId, conversationId, fileName);
              console.log("✅ Image deleted from storage:", fileName);
            }
          } else if (
            block.type === "file" &&
            block.source_type === "storage" &&
            block.mime_type === "application/pdf"
          ) {
            const pdfBlock = block as StoragePdfBlock;
            await deletePdfFromStorage(
              userId,
              conversationId,
              pdfBlock.fileName,
            );
            console.log("✅ PDF deleted from storage:", pdfBlock.fileName);
          }

          if (
            captureMode &&
            "source_type" in block &&
            block.source_type === "storage" &&
            "messageId" in block &&
            block.messageId
          ) {
            await deleteCaptureMessage(userId, conversationId, block.messageId);
            console.log("✅ Message deleted from Firestore:", block.messageId);
          }
        } catch (error) {
          // Ignore "object-not-found" errors (file already deleted)
          const isNotFoundError =
            error instanceof Error &&
            (error.message.includes("object-not-found") ||
              error.message.includes("does not exist"));

          if (!isNotFoundError) {
            console.warn(
              "Failed to delete file from storage/Firestore:",
              error,
            );
          }
        }
      }

      removeBlockFromHook(index);
    },
    [contentBlocks, userId, conversationId, captureMode, removeBlockFromHook],
  );

  // Check if all storage blocks have completed upload AND Firestore message creation
  // Storage blocks need messageId set (happens after createCaptureContentMessage succeeds)
  // Also blocks any blocks that are currently uploading (uploading flag or uploadProgress exists)
  // Also blocks any blocks where Firestore message creation is in progress (creatingMessage flag)
  const allStorageBlocksHaveFirestoreMessages = useMemo(() => {
    if (!captureMode) return true; // Not in capture mode, no blocking needed

    return contentBlocks.every((block) => {
      // Block if any file is currently uploading (has uploadProgress or uploading flag)
      if ("uploadProgress" in block && block.uploadProgress !== undefined) {
        return false; // Still uploading to Storage
      }
      if ("uploading" in block && block.uploading) {
        return false; // Still uploading
      }

      // Block if Firestore message creation is in progress
      if ("creatingMessage" in block && block.creatingMessage) {
        return false; // Still creating Firestore message
      }

      // Check if this is a storage block (image, audio, or PDF)
      const isStorageBlock =
        "source_type" in block && block.source_type === "storage";

      if (!isStorageBlock) {
        return true; // Non-storage blocks don't need messageId
      }

      // Storage block must have messageId (set after Firestore creation)
      return !!(block as any).messageId;
    });
  }, [contentBlocks, captureMode]);

  // Check for at least one audio recording in content blocks (required in capture mode)
  const hasAudioRecordings = useMemo(() => {
    return contentBlocks.some(
      (block) =>
        block.type === "audio" &&
        "source_type" in block &&
        block.source_type === "storage",
    );
  }, [contentBlocks]);

  // Check for at least one image in content blocks (recommended in capture mode)
  const hasImages = useMemo(() => {
    return contentBlocks.some((block) => block.type === "image");
  }, [contentBlocks]);

  // Check if any blocks are still being processed (instant preview feature)
  const hasProcessingBlocks = useMemo(() => {
    return contentBlocks.some(
      (block) => "processing" in block && block.processing === true,
    );
  }, [contentBlocks]);

  // In capture mode: require all uploads complete AND at least one audio recording
  // In normal mode: require content AND no pending uploads
  const canSubmit = captureMode
    ? !isLoading &&
      !isRecording &&
      !isUploadingAudio &&
      !hasPendingUploads &&
      !hasProcessingBlocks &&
      allStorageBlocksHaveFirestoreMessages &&
      hasAudioRecordings // Audio is mandatory in capture mode
    : (input.trim().length > 0 || contentBlocks.length > 0) &&
      !isLoading &&
      !isRecording &&
      !isUploadingAudio &&
      !hasPendingUploads &&
      !hasProcessingBlocks &&
      allStorageBlocksHaveFirestoreMessages;

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;

      if (onBeforeSubmit) {
        try {
          await onBeforeSubmit();
        } catch (error) {
          console.warn(
            "[useChatInput] Pre-submit action failed, continuing with message:",
            error,
          );
        }
      }

      onResetFirstToken();

      // Create new human message (images/documents FIRST, then text)
      const newHumanMessage: Message = {
        id: uuidv4(),
        type: "human",
        content: [
          ...contentBlocks,
          ...(input.trim().length > 0 ? [{ type: "text", text: input }] : []),
        ] as Message["content"],
      };

      // Backend now saves user messages to Firestore with frontend-provided ID
      // Frontend only provides optimistic update for instant UI feedback

      const toolMessages = ensureToolCallsHaveResponses(messages as any);
      const context =
        Object.keys(artifactContext).length > 0 ? artifactContext : undefined;

      const submitPayload = {
        messages: [...toolMessages, newHumanMessage],
        context,
      };

      onSubmit(submitPayload, {
        streamMode: ["values"],
        streamSubgraphs: true,
        streamResumable: true,
        optimisticValues: (prev: any) => ({
          ...prev,
          context,
          messages: [
            ...(prev.messages ?? []),
            ...toolMessages,
            newHumanMessage,
          ],
        }),
      });

      // Clear form
      setInput("");
      setContentBlocks([]);
      try {
        sessionStorage.removeItem(getInputStorageKey(conversationId));
        sessionStorage.removeItem(getBlocksStorageKey(conversationId));
      } catch {
        // Ignore storage errors
      }
    },
    [
      canSubmit,
      onBeforeSubmit,
      onResetFirstToken,
      input,
      contentBlocks,
      messages,
      artifactContext,
      onSubmit,
      setContentBlocks,
      conversationId,
    ],
  );

  const resetBlocks = useCallback(() => {
    resetBlocksFromHook();
  }, [resetBlocksFromHook]);

  return {
    // Input state
    input,
    setInput,

    // File upload
    contentBlocks,
    setContentBlocks,
    handleFileUpload,
    dropRef,
    removeBlock,
    resetBlocks,
    dragOver,
    handlePaste,
    updateBlockDescription,

    // Audio recording
    isRecording,
    recordingDuration,
    analyserNode,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    isUploadingAudio,

    // Form submission
    handleSubmit,
    canSubmit,

    // Capture mode validation helpers
    hasAudioRecordings,
    hasImages,
  };
}
